import { Component, computed, nextTick, watch, WatchStopHandle } from 'vue';
import { IRouter, Routes, TRoutes } from '@/models/router/router.interface';
import { ModalErrorStates, ModalUnsafeExitStates } from '@/models/modal/modal.enum';
import { NotificationStatuses } from '@/models/notifications.interface';
import { LoadingStatuses } from '@/models/loading.interface';

import LoadForCampaignCreation from '@/presentation/pages/load-for-campaign-creation/load-for-campaign-creation.vue';
import General from '@/presentation/pages/general/general.vue';
import LinkedOrganisations from '@/presentation/pages/linked-organisations/linked-organisations.vue';
import EntitiesAndGeotargeting from '@/presentation/pages/entities-and-geotargeting/entities-and-geotargeting.vue';
import CustomProducts from '@/presentation/pages/custom-products/custom-products.vue';
import Rights from '@/presentation/pages/rights/rights.vue';
import InternalNotes from '@/presentation/pages/internal-notes/internal-notes.vue';
import Auditor from '@/presentation/pages/auditor/auditor.vue';

import InitialRoutePresenter from '@/presenters/initial-route-presenter';
import navigationPresenter from '@/presenters/navigation-presenter';
import campaignPresenter from '@/presenters/campaign-presenter';
import store from '@/store';

import geotargetingPresenter from '@/presenters/geotargeting-presenter';

export default class AppCode {
	private routes: TRoutes = {
		[Routes.LOAD_FOR_CAMPAIGN_CREATION]: LoadForCampaignCreation,
		[Routes.GENERAL]: General,
		[Routes.LINKED_ORGANISATIONS]: LinkedOrganisations,
		[Routes.ENTITIES_AND_GEOTARGETING]: EntitiesAndGeotargeting,
		[Routes.CUSTOM_PRODUCTS]: CustomProducts,
		[Routes.RIGHTS]: Rights,
		[Routes.INTERNAL_NOTES]: InternalNotes,
		[Routes.AUDITOR]: Auditor
	};
	private unwatch: WatchStopHandle | null = null;

	public standAloneLogin = computed((): boolean => {
		return store.getters.standAloneLogin();
	});

	// eslint-disable-next-line @typescript-eslint/naming-convention
	public CurrentComponent = computed((): Component => {
		return this.routes[this.router.value.current];
	});

	public router = computed((): IRouter => {
		return store.getters.router();
	});

	public isCampaignCreation = computed((): boolean => {
		return store.getters.isCampaignCreation();
	});

	public isLoading = computed((): boolean => {
		const loadingStatus = store.getters.loadingStatusPrimaryData();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	public isOverlap = computed((): boolean => {
		const overlappingCampaignsCluster = store.getters.overlappingCampaignsCluster();
		return overlappingCampaignsCluster !== null;
	});

	public errorActive = computed((): boolean => {
		return this.modalErrorState.value !== ModalErrorStates.INACTIVE;
	});

	public unsafeExitActive = computed((): boolean => {
		return this.modalUnsafeExitState.value !== ModalUnsafeExitStates.INACTIVE;
	});

	public notificationActive = computed((): boolean => {
		return this.notificationStatus.value !== NotificationStatuses.INACTIVE;
	});

	private modalErrorState = computed((): ModalErrorStates => {
		return store.getters.modalErrorState();
	});

	private modalUnsafeExitState = computed((): ModalUnsafeExitStates => {
		return store.getters.modalUnsafeExitState();
	});

	private notificationStatus = computed((): NotificationStatuses => {
		return store.getters.notificationStatus();
	});

	public async mounted(): Promise<void> {
		geotargetingPresenter.updateStoreWhenGeotargetingMicroserviceIsReady();
		InitialRoutePresenter.prepareForRoutingActions();
		await this.loadData();

		this.unwatch = watch(this.isCampaignCreation, this.loadDataWhenCampaignIsCreated.bind(this));
	}

	public unmounted(): void {
		if (!this.unwatch) {
			return;
		}

		this.unwatch();
	}

	private loadDataWhenCampaignIsCreated(newValue: boolean, oldValue: boolean): void {
		if (!oldValue || newValue) {
			return;
		}

		void this.loadData();
	}

	private async loadData(): Promise<void> {
		await nextTick();

		if (this.isCampaignCreation.value) {
			return;
		}

		navigationPresenter.interruptClosingOfPageIfUnsavedChanges();

		await this.loadEssentialData();

		if (this.errorActive.value) {
			return;
		}

		void this.loadOptionalData();
	}

	private async loadEssentialData(): Promise<void> {
		// Note: We need custom products data for the budget left calculation. To avoid unnecessary complexity, we load it on every page.
		await Promise.all([
			campaignPresenter.prepareCampaignDetails(),
			campaignPresenter.retrieveCustomProducts()
		]);

		campaignPresenter.updateOriginalCampaignData();
	}

	private async loadOptionalData(): Promise<void> {
		await campaignPresenter.updateCampaignReadinessInfo();
	}
}
